// SergeantTap diagnostic trace client (OBSERVABILITY ONLY).
// OFF unless the app was opened with ?diag=1 (persisted in sessionStorage for the session).
// Buffers events locally and flushes to /api/diag/events so a 520/origin outage stays visible
// after recovery. Never stores PII/prose/secrets — only structured, safe metadata.
import axios from 'axios';
import { BUILD_INFO } from '../buildInfo';

const API = process.env.REACT_APP_BACKEND_URL;
const SS_ON = 'st_diag_on';
const SS_SID = 'st_diag_session';
const SS_BUF = 'st_diag_buffer';

let _seq = 0;
let _reqSeq = 0;
let _clockOffsetMs = 0;
let _started = false;
let _curSeekerVersion = null; // updated by components for stale-response-race detection

const uuid = () =>
  (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export function isDiagOn() {
  try { return sessionStorage.getItem(SS_ON) === '1'; } catch { return false; }
}
export function getSession() {
  try { return sessionStorage.getItem(SS_SID); } catch { return null; }
}
export function setCurrentSeekerVersion(v) { _curSeekerVersion = v || null; }

function readBuf() {
  try { return JSON.parse(sessionStorage.getItem(SS_BUF) || '[]'); } catch { return []; }
}
function writeBuf(arr) {
  try { sessionStorage.setItem(SS_BUF, JSON.stringify(arr.slice(-1500))); } catch {}
}

// Public: record a safe diagnostic event (buffered + best-effort flushed).
export function logEvent(event_type, payload = {}) {
  if (!isDiagOn()) return;
  const ev = {
    event_id: uuid(),
    event_type,
    ts: new Date(Date.now() + 0).toISOString(),
    seq: ++_seq,
    payload: { ...payload },
  };
  const buf = readBuf();
  buf.push(ev);
  writeBuf(buf);
  flush();
}

let _flushing = false;
export async function flush() {
  if (!isDiagOn() || _flushing) return;
  const sid = getSession();
  const buf = readBuf();
  if (!sid || buf.length === 0) return;
  _flushing = true;
  const batch = buf.slice(0, 50);
  try {
    await axios.post(`${API}/api/diag/events`, { session_id: sid, events: batch },
      { withCredentials: true, timeout: 15000, headers: { 'X-ST-Diagnostic-Session': sid } });
    // remove flushed (dedup is server-side by event_id, so safe)
    const remaining = readBuf().filter(e => !batch.find(b => b.event_id === e.event_id));
    writeBuf(remaining);
  } catch {
    // origin/outage: keep buffered; will retry on next event / reconnect
  } finally {
    _flushing = false;
  }
}

async function estimateClock() {
  try {
    const t0 = Date.now();
    const r = await axios.get(`${API}/api/diag/clock`, { withCredentials: true, timeout: 8000 });
    const t1 = Date.now();
    const rtt = t1 - t0;
    const server = new Date(r.data.server_ts).getTime();
    _clockOffsetMs = server - (t0 + rtt / 2);
    logEvent('clock_offset_estimated', { offset_ms: Math.round(_clockOffsetMs), rtt_ms: rtt,
      runtime_instance_id: r.data.runtime_instance_id });
  } catch {}
}

// Lightweight health probe around failures (spec 8).
export async function diagHealthCheck(tag) {
  if (!isDiagOn()) return;
  const t0 = performance.now();
  try {
    const r = await axios.get(`${API}/api/diag/health`, { withCredentials: true, timeout: 8000 });
    logEvent('health_check', { tag, ok: true, status: 200,
      duration_ms: Math.round(performance.now() - t0),
      runtime_instance_id: r.data.runtime_instance_id });
  } catch (e) {
    logEvent('health_check', { tag, ok: false,
      status: e.response?.status || e.code || 'error',
      duration_ms: Math.round(performance.now() - t0) });
  }
}

function perfTiming(url) {
  try {
    const entries = performance.getEntriesByType('resource').filter(e => e.name.includes(url));
    const e = entries[entries.length - 1];
    if (!e) return {};
    return {
      request_start_ms: Math.round(e.requestStart || 0),
      response_start_ms: Math.round(e.responseStart || 0),
      response_end_ms: Math.round(e.responseEnd || 0),
      waiting_server_ms: Math.round((e.responseStart || 0) - (e.requestStart || 0)),
      transfer_ms: Math.round((e.responseEnd || 0) - (e.responseStart || 0)),
      total_ms: Math.round(e.duration || 0),
    };
  } catch { return {}; }
}

// Install a GLOBAL axios interceptor that only acts in diag mode. Adds correlation headers,
// assigns a monotonic frontend request sequence, and logs HTTP start/complete/fail with network
// timing, CDN/cache headers, status classification, and stale-response-race detection.
function installInterceptors() {
  axios.interceptors.request.use((config) => {
    if (isDiagOn() && (config.url || '').includes('/api/')) {
      const sid = getSession();
      const rid = uuid();
      config.headers = config.headers || {};
      config.headers['X-ST-Diagnostic-Session'] = sid;
      config.headers['X-ST-Diagnostic-Request'] = rid;
      config.__diag = { rid, start: Date.now(), reqSeq: ++_reqSeq,
        seekerVersionAtStart: _curSeekerVersion, url: config.url };
      // avoid recursive logging of the ingest endpoint itself
      if (!config.url.includes('/api/diag/events')) {
        logEvent('http_started', { request_id: rid, url: short(config.url),
          method: (config.method || 'get').toUpperCase(), req_seq: config.__diag.reqSeq,
          seeker_version_at_start: _curSeekerVersion,
          visibility: document.visibilityState, focused: document.hasFocus() });
      }
    }
    return config;
  });
  axios.interceptors.response.use(
    (resp) => { logHttp(resp.config, resp, null); return resp; },
    (error) => { logHttp(error.config, error.response, error); return Promise.reject(error); }
  );
}

function short(u) { try { return (u || '').replace(API, ''); } catch { return u; } }

function logHttp(config, resp, error) {
  if (!config || !config.__diag || !isDiagOn()) return;
  if ((config.url || '').includes('/api/diag/events')) return;
  const d = config.__diag;
  const h = resp?.headers || {};
  const isTimeout = error?.code === 'ECONNABORTED';
  const status = resp?.status || error?.code || 'network_error';
  const is520 = resp?.status === 520 || /520/.test(String(status));
  const seekerVersionReturned =
    resp?.data?.seeker_version || resp?.data?.c_seeker_version || null;
  const applied = d.seekerVersionAtStart == null ||
    seekerVersionReturned == null || seekerVersionReturned === _curSeekerVersion;
  logEvent(error ? 'http_failed' : 'http_completed', {
    request_id: d.rid, url: short(d.url), req_seq: d.reqSeq,
    duration_ms: Date.now() - d.start,
    status, econnaborted: !!isTimeout, is_520: !!is520,
    error_code: error?.code, error_name: error?.name,
    ray_id: h['cf-ray'], cf_cache_status: h['cf-cache-status'], age: h['age'],
    etag: h['etag'], server: h['server'], cache_control: h['cache-control'],
    returned_error_count: resp?.data?.errors?.length ?? null,
    roles_regenerated: resp?.data?.roles_regenerated?.length ?? null,
    summary_regenerated: resp?.data?.summary_regenerated ?? null,
    // stale-response-race (spec 34)
    seeker_version_at_start: d.seekerVersionAtStart,
    seeker_version_returned: seekerVersionReturned,
    current_seeker_version_on_arrival: _curSeekerVersion,
    response_applied: applied,
    net: perfTiming(d.url),
    visibility: document.visibilityState,
  });
  // Health probe immediately after an origin/network failure (spec 8), but NEVER for a failing
  // diagnostic endpoint itself. A failed /api/diag/health (or /clock, /events) must not spawn
  // another health probe — otherwise an unhealthy origin creates a recursive request storm that
  // amplifies the outage. At most one bounded probe is generated per ordinary triggering failure.
  const isDiagEndpoint = (d.url || '').includes('/api/diag/');
  if (error && !isDiagEndpoint && (isTimeout || is520 || status === 'network_error')) {
    diagHealthCheck('after_failure');
  }
}

export function exportTrace() {
  const sid = getSession();
  // flush local buffer first (best-effort), then pull the assembled server export + merge locals.
  return (async () => {
    await flush();
    let server = null;
    try {
      const r = await axios.get(`${API}/api/diag/export?session=${encodeURIComponent(sid)}`,
        { withCredentials: true, timeout: 20000, headers: { 'X-ST-Diagnostic-Session': sid } });
      server = r.data;
    } catch {
      server = { note: 'server export unavailable (origin offline?) — local buffer included' };
    }
    const bundle = {
      diagnostic_session: { session_id: sid, exported_at: new Date().toISOString() },
      frontend_build_identity: {
        ...BUILD_INFO,
        main_js_assets: [...document.querySelectorAll('script[src]')].map(s => s.src).slice(0, 8),
        service_worker_controlled: !!navigator.serviceWorker?.controller,
        service_worker_url: navigator.serviceWorker?.controller?.scriptURL || null,
        navigation_type: (performance.getEntriesByType('navigation')[0] || {}).type,
        backend_origin: API,
        clock_offset_ms: Math.round(_clockOffsetMs),
      },
      local_buffered_events: readBuf(),
      server_export: server,
    };
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sergeanttap-trace-${sid}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
    return bundle;
  })();
}

export function enableFromQuery() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('diag') === '1' && !isDiagOn()) {
      sessionStorage.setItem(SS_ON, '1');
      sessionStorage.setItem(SS_SID, uuid());
    }
  } catch {}
}

export function endDiagnostics() {
  try {
    [SS_ON, SS_SID, SS_BUF].forEach(k => sessionStorage.removeItem(k));
  } catch {}
}

// one-time init (safe to call on every mount)
export function initDiagnostics() {
  enableFromQuery();
  if (_started) return;
  _started = true;
  installInterceptors();
  if (isDiagOn()) {
    estimateClock();
    logEvent('diag_session_started', {
      build: BUILD_INFO, url: window.location.pathname,
      authenticated: !!(document.cookie || '').length });
    ['visibilitychange'].forEach(() =>
      document.addEventListener('visibilitychange', () =>
        logEvent('page_visibility', { state: document.visibilityState })));
    window.addEventListener('focus', () => logEvent('window_focus', { focused: true }));
    window.addEventListener('blur', () => logEvent('window_focus', { focused: false }));
    window.addEventListener('online', () => { logEvent('connectivity', { online: true }); flush(); diagHealthCheck('reconnect'); });
    window.addEventListener('offline', () => logEvent('connectivity', { online: false }));
    window.addEventListener('pageshow', (e) => logEvent('pageshow', { persisted: e.persisted }));
  }
}
